/**
 * Parallel Provider Sets — admin page for the parallel-set registry.
 *
 * A parallel set is a named list of 2–5 provider keys. When a tier slot is
 * configured as `kind: 'parallel'` with `parallelSet: '<name>'`, the pipeline
 * fan-out resolver expands that slot into N runs (one per branch) and the
 * collapse stage picks a winner per the set's `selection` strategy.
 *
 * See docs/FeatureDesigns/PipelineInfra/analysis-routing.md (§3.3, §4.3) for the design.
 * Phase B ships the authoring UI; Phase C wires the fan-out + collapse.
 *
 * v1 only supports `selection: 'default'`. The `'best-result'` option is
 * rejected by the server validator and disabled in the UI.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { ProviderConfig, ProviderParallelSet } from '../../types/settings';
import type { SlotParams } from '@padcanvas/shared';
import { branchKey as branchId } from '@padcanvas/shared';
import { SlotParamsFields } from './SlotParamsFields';
import { DataTable, type ColumnDef, Modal, Button, Input } from '@shared-ui/components';
import { useProviderSetUsage, UsedByCell } from './UsedByCell';
import './ProviderTieredSets.css';

const MIN_BRANCHES = 2;
const MAX_BRANCHES = 20;

function emptySet(): ProviderParallelSet {
  return {
    name: '',
    description: '',
    branches: [],
    defaultBranch: '',
    selection: 'default',
  };
}

/** Comparable shape for the dirty check — fixed key order, absent collapsed
 *  to '' so a stored set missing `description` doesn't read as edited. */
function normalizeForCompare(set?: ProviderParallelSet) {
  if (!set) return null;
  return {
    name: set.name ?? '',
    description: set.description ?? '',
    branches: set.branches ?? [],
    defaultBranch: set.defaultBranch ?? '',
    selection: set.selection ?? 'default',
  };
}

export function ProviderParallelSets() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; original?: string; draft: ProviderParallelSet } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: setsData, isLoading } = useQuery({
    queryKey: ['provider-parallel-sets'],
    queryFn: () => adminApi.listProviderParallelSets(),
  });
  const { data: providersData } = useQuery({
    queryKey: ['providers'],
    queryFn: () => adminApi.getProviderConfig(),
  });

  const sets = setsData?.sets ?? [];

  // Tiered sets are needed to attribute indirect use: a parallel set reached
  // only through a tier slot is still in use by that pipeline.
  const { data: tieredSetsData } = useQuery({
    queryKey: ['provider-tiered-sets'],
    queryFn: () => adminApi.listProviderTieredSets(),
  });
  const usage = useProviderSetUsage(tieredSetsData?.sets ?? []);
  const providers: ProviderConfig[] = providersData?.providers ?? [];
  const activeProviders = providers.filter(p => p.status !== 'deprecated');

  // Group providers by family for the branch picker. Order families
  // deterministically so the picker layout is stable across renders.
  const FAMILY_ORDER: readonly ProviderConfig['family'][] = ['anthropic', 'openai', 'google', 'xai', 'deepseek', 'mock'] as const;
  const FAMILY_LABEL: Record<ProviderConfig['family'], string> = {
    anthropic: 'Anthropic',
    openai: 'OpenAI',
    google: 'Google',
    xai: 'xAI',
    deepseek: 'DeepSeek',
    mock: 'Mock',
  };
  const providersByFamily = useMemo(() => {
    const map = new Map<ProviderConfig['family'], ProviderConfig[]>();
    for (const p of activeProviders) {
      const list = map.get(p.family) ?? [];
      list.push(p);
      map.set(p.family, list);
    }
    // Sort each family alphabetically by key.
    for (const list of map.values()) list.sort((a, b) => a.key.localeCompare(b.key));
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers]);

  const createMut = useMutation({
    mutationFn: (set: ProviderParallelSet) => adminApi.createProviderParallelSet(set),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-parallel-sets'] });
      queryClient.invalidateQueries({ queryKey: ['provider-tiered-sets'] });
      setEditing(null); setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });
  const updateMut = useMutation({
    mutationFn: ({ name, set }: { name: string; set: ProviderParallelSet }) => adminApi.updateProviderParallelSet(name, set),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-parallel-sets'] });
      queryClient.invalidateQueries({ queryKey: ['provider-tiered-sets'] });
      setEditing(null); setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });
  const deleteMut = useMutation({
    mutationFn: (name: string) => adminApi.deleteProviderParallelSet(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-parallel-sets'] });
      queryClient.invalidateQueries({ queryKey: ['provider-tiered-sets'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  function handleSave() {
    if (!editing) return;
    setError(null);
    if (editing.mode === 'create') {
      createMut.mutate(editing.draft);
    } else {
      updateMut.mutate({ name: editing.original!, set: editing.draft });
    }
  }

  function handleDelete(name: string) {
    if (!confirm(`Delete parallel set "${name}"? This fails if any tiered set's tier slot still references it.`)) return;
    deleteMut.mutate(name);
  }

  function updateDraft(patch: Partial<ProviderParallelSet>) {
    if (!editing) return;
    setEditing({ ...editing, draft: { ...editing.draft, ...patch } });
  }

  function handleCancel() {
    if (!editing) return;
    // Against what the modal opened with, not against emptiness — an existing
    // set is never empty, so the old check prompted on every cancel.
    const baseline = editing.mode === 'edit'
      ? sets.find(s => s.name === editing.original)
      : emptySet();
    const dirty = JSON.stringify(normalizeForCompare(editing.draft)) !== JSON.stringify(normalizeForCompare(baseline));
    if (dirty && !confirm('Discard changes?')) return;
    setEditing(null);
  }

  function toggleBranch(key: string) {
    if (!editing) return;
    const { branches, defaultBranch } = editing.draft;
    if (branches.some(b => b.provider === key)) {
      // Removing a provider removes every branch on it, including duplicates.
      const nextBranches = branches.filter(b => b.provider !== key);
      const stillThere = nextBranches.some(b => branchId(b) === defaultBranch);
      updateDraft({
        branches: nextBranches,
        defaultBranch: stillThere ? defaultBranch : (nextBranches[0] ? branchId(nextBranches[0]) : ''),
      });
    } else {
      if (branches.length >= MAX_BRANCHES) return;
      updateDraft({
        branches: [...branches, { provider: key }],
        defaultBranch: defaultBranch || key,
      });
    }
  }

  /** Add a second branch on a provider already in the set — the "same model,
   *  different params" comparison. Both branches then need labels to be
   *  distinguishable, so seed them rather than making the user invent two. */
  function duplicateBranch(provider: string) {
    if (!editing) return;
    const { branches, defaultBranch } = editing.draft;
    if (branches.length >= MAX_BRANCHES) return;
    const existing = branches.filter(b => b.provider === provider);
    const next = branches.map(b =>
      b.provider === provider && !b.label ? { ...b, label: `${provider}-a` } : b);
    const suffix = String.fromCharCode(97 + existing.length); // a, b, c…
    updateDraft({
      branches: [...next, { provider, label: `${provider}-${suffix}` }],
      // The default followed the bare provider key; it now names a labelled branch.
      defaultBranch: defaultBranch === provider ? `${provider}-a` : defaultBranch,
    });
  }

  /** Rename a branch. Keeps `defaultBranch` pointing at the same branch, and
   *  drops a label equal to the provider key so it stays the implicit default. */
  function setBranchLabel(id: string, label: string) {
    if (!editing) return;
    const { branches, defaultBranch } = editing.draft;
    const trimmed = label.trim();
    const nextBranches = branches.map(b => {
      if (branchId(b) !== id) return b;
      const { label: _drop, ...rest } = b;
      return trimmed && trimmed !== b.provider ? { ...rest, label: trimmed } : rest;
    });
    const idx = branches.findIndex(b => branchId(b) === id);
    updateDraft({
      branches: nextBranches,
      defaultBranch: defaultBranch === id && idx >= 0 ? branchId(nextBranches[idx]) : defaultBranch,
    });
  }

  /** Replace one branch's params. Undefined/empty clears them, so an emptied
   *  form persists as "inherit the provider default" rather than an empty object. */
  function setBranchParams(id: string, params: SlotParams | undefined) {
    if (!editing) return;
    updateDraft({
      branches: editing.draft.branches.map(b =>
        branchId(b) === id ? { ...b, ...(params ? { params } : { params: undefined }) } : b),
    });
  }

  function setDefault(id: string) {
    if (!editing) return;
    if (!editing.draft.branches.some(b => branchId(b) === id)) return;
    updateDraft({ defaultBranch: id });
  }

  const columns: ColumnDef<ProviderParallelSet>[] = useMemo(() => [
    { key: 'name', header: 'Name', render: (s) => <strong>{s.name}</strong> },
    {
      key: 'branches',
      header: 'Providers',
      render: (s) => (
        <div className="provider-sets-level">
          {s.branches.map(b => (
            <span key={branchId(b)} className="provider-sets-provider" style={{ fontWeight: branchId(b) === s.defaultBranch ? 700 : 400 }}>
              {branchId(b)}{branchId(b) === s.defaultBranch ? ' ★' : ''}{b.params ? ' ⚙' : ''}
            </span>
          ))}
        </div>
      ),
    },
    { key: 'selection', header: 'Selection', render: (s) => <code>{s.selection}</code> },
    {
      key: 'usedBy',
      header: 'Used by',
      className: 'provider-sets-usedby',
      render: (s) => <UsedByCell usages={usage.parallel.get(s.name)} loading={usage.loading} />,
    },
    {
      key: 'actions',
      header: '',
      render: (s) => (
        <div className="provider-sets-row-actions">
          <Button
            variant="danger"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleDelete(s.name); }}
            disabled={deleteMut.isPending}
          >
            Delete
          </Button>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [deleteMut.isPending, usage.parallel, usage.loading]);

  if (isLoading) return <div className="provider-sets-loading">Loading parallel provider sets…</div>;

  const draft = editing?.draft;
  const branchCountOk = draft ? draft.branches.length >= MIN_BRANCHES && draft.branches.length <= MAX_BRANCHES : false;
  const canSave = !!draft
    && !!draft.name
    && branchCountOk
    && !!draft.defaultBranch
    && draft.branches.some(b => b.provider === draft.defaultBranch);

  return (
    <div className="provider-sets-page">
      <div className="provider-sets-header">
        <div>
          <h2>Parallel Provider Sets</h2>
          <p className="provider-sets-subtitle">
            Named lists of 2–5 providers that run in parallel for a single tier slot. Referenced from tiered sets (tier slot kind = parallel).
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => {
          setEditing({ mode: 'create', draft: emptySet() });
        }}>+ New Parallel Set</Button>
      </div>

      {error && <div className="provider-sets-error">{error}</div>}

      <DataTable
        columns={columns}
        data={sets}
        rowKey={(s) => s.name}
        onRowClick={(s) => {
          setEditing({ mode: 'edit', original: s.name, draft: structuredClone(s) });
        }}
        loading={isLoading}
        emptyMessage='No parallel provider sets yet. Click "+ New Parallel Set" to create one.'
        compact
        bordered
      />

      {editing && draft && (
        <Modal
          isOpen={true}
          onClose={handleCancel}
          title={editing.mode === 'create' ? 'New Parallel Provider Set' : `Edit "${editing.original}"`}
          size="lg"
          closeOnOverlayClick={false}
          closeOnEscape={false}
        >
          <div className="provider-sets-form">
            <label>
              Name
              <Input
                type="text"
                value={draft.name}
                placeholder="e.g. frontier-3"
                onChange={e => updateDraft({ name: e.target.value })}
                disabled={editing.mode === 'edit'}
              />
            </label>
            <label>
              Description
              <textarea
                rows={2}
                value={draft.description ?? ''}
                onChange={e => updateDraft({ description: e.target.value })}
              />
            </label>

            <div className="branches-picker">
              <div className="branches-picker-header">
                <span className="branches-picker-title">Providers</span>
              </div>
              <div className="branches-picker-list">
                {FAMILY_ORDER.map(family => {
                  const list = providersByFamily.get(family);
                  if (!list || list.length === 0) return null;
                  return (
                    <div key={family} className="branches-picker-group">
                      <div className="branches-picker-group-header">{FAMILY_LABEL[family]}</div>
                      {list.map(p => {
                        const key = p.key;
                        // A provider can appear more than once — same model,
                        // different params. One row per branch in that case.
                        const mine = draft.branches.filter(b => b.provider === key);
                        const checked = mine.length > 0;
                        const atCap = !checked && draft.branches.length >= MAX_BRANCHES;
                        return (
                          <div key={key} className={`branches-picker-item ${checked ? 'selected' : ''}`}>
                            <label className="branches-picker-check">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={atCap}
                                onChange={() => toggleBranch(key)}
                              />
                              <span className="branches-picker-name">{key}</span>
                            </label>
                            {checked && (
                              <button
                                type="button"
                                className="branches-picker-dup"
                                onClick={() => duplicateBranch(key)}
                                disabled={draft.branches.length >= MAX_BRANCHES}
                                title="Add a second branch on this model, to compare parameter settings against each other"
                              >
                                ＋
                              </button>
                            )}
                            {mine.map(b => {
                              const id = branchId(b);
                              const isDefault = draft.defaultBranch === id;
                              return (
                                <div key={id} className="branches-picker-branch">
                                  {/* Only ask for a label when the provider key
                                      can no longer identify the branch. */}
                                  {mine.length > 1 && (
                                    <Input
                                      type="text"
                                      value={b.label ?? ''}
                                      placeholder={key}
                                      onChange={e => setBranchLabel(id, e.target.value)}
                                    />
                                  )}
                                  <button
                                    type="button"
                                    className={`branches-picker-star ${isDefault ? 'active' : ''}`}
                                    onClick={() => setDefault(id)}
                                    aria-label={isDefault ? 'Default branch' : 'Set as default'}
                                    title={isDefault ? 'Default branch' : 'Set as default'}
                                  >
                                    {isDefault ? '★' : '☆'}
                                  </button>
                                  <SlotParamsFields
                                    provider={p}
                                    params={b.params}
                                    onChange={next => setBranchParams(id, next)}
                                    disabled={createMut.isPending || updateMut.isPending}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div className="branches-picker-hint">
                {draft.branches.length < MIN_BRANCHES
                  ? `Select at least ${MIN_BRANCHES} providers.`
                  : !draft.defaultBranch
                    ? 'Mark one provider as default (★).'
                    : `Default: ${draft.defaultBranch}`}
              </div>
            </div>

            <div className="provider-sets-form-actions">
              <Button variant="secondary" size="sm" onClick={handleCancel}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={!canSave || createMut.isPending || updateMut.isPending}
              >
                {createMut.isPending || updateMut.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
